import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { ArrowLeft, Wrench, CheckCircle, Clock, DollarSign } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { tenantTodayDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency } from '../../lib/display-labels';

const STATUS_FILTERS = ['All', 'Pending', 'In Progress', 'Completed'];

function StatusBadge({ status }: { status: string }) {
  const lower = (status || '').toLowerCase();
  const color = lower.includes('completed') || lower === 'complete'
    ? 'bg-green-500/20 text-green-400'
    : lower.includes('progress')
      ? 'bg-blue-500/20 text-blue-400'
      : 'bg-amber-500/20 text-amber-400';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>{status}</span>;
}

/** Whole-dollar money. @see fmtCurrency — absence renders '—', not '$0'. */
const fmt = (n: number | string | null | undefined) => fmtCurrency(n, null, 0);

export default function MaintenanceSummaryReport() {
  const navigate = useNavigate();
  // The TENANT's current month (companies.time_zone), not the device's. No
  // zone, no seeded range — the inputs start blank rather than guess.
  const timeZone = useTenantTimeZone();
  const now = tenantTodayDate(timeZone);
  const [startDate, setStartDate] = useState(now ? format(startOfMonth(now), 'yyyy-MM-dd') : '');
  const [endDate, setEndDate] = useState(now ? format(endOfMonth(now), 'yyyy-MM-dd') : '');
  const [statusFilter, setStatusFilter] = useState('All');

  const { data, isLoading } = useQuery({
    queryKey: ['maintenance-summary', startDate, endDate],
    queryFn: () => apiClient.get('/surface-rentals/reports/maintenance-summary', { params: { start_date: startDate, end_date: endDate } }).then(r => unwrap<any>(r.data)),
  });

  const workOrders: any[] = useMemo(() => {
    let list = data?.work_orders || [];
    if (statusFilter !== 'All') {
      list = list.filter((w: any) => (w.status || '').toLowerCase().includes(statusFilter.toLowerCase()));
    }
    return list;
  }, [data, statusFilter]);

  // API: { period, work_orders, summary: { total_orders, completed, pending,
  // total_cost, assets_affected, avg_turnaround_days } }. The old code read all
  // four off the top level. total_orders is also named differently from the
  // total_work_orders it looked for, so even the shape guess was wrong.
  const summary = data?.summary;
  const totalWO = summary?.total_orders ?? workOrders.length;
  // The server counts a work order as completed on status 'approved' OR a
  // completed_at timestamp; matching the literal word "completed" against
  // status found neither, so this tile read 0 whenever the summary was missed.
  const completed = summary?.completed ?? workOrders.filter((w: any) => w.status === 'approved' || w.completed_at).length;
  const pending = summary?.pending ?? workOrders.filter((w: any) => w.status === 'open' || w.status === 'in_progress').length;
  // The API's figure or a dash. It used to fall back to summing the rows here
  // with `w.cost || 0`, which printed $0 for a total nobody had computed.
  const totalCost = summary?.total_cost ?? null;

  const isEmpty = !isLoading && workOrders.length === 0;

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background">
      
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate('/reports')} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Maintenance Summary</h1>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent" />
          <span className="text-fieldiq-muted text-xs">to</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent" />
        </div>

        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {STATUS_FILTERS.map(f => (
            <button key={f} onClick={() => setStatusFilter(f)} className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${statusFilter === f ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-card text-fieldiq-muted hover:text-white'}`}>{f}</button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <Wrench className="w-4 h-4 text-fieldiq-accent mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{totalWO}</p>
            <p className="text-[10px] text-fieldiq-muted">Total Work Orders</p>
          </div>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <CheckCircle className="w-4 h-4 text-green-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-green-400">{completed}</p>
            <p className="text-[10px] text-fieldiq-muted">Completed</p>
          </div>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <Clock className="w-4 h-4 text-amber-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-amber-400">{pending}</p>
            <p className="text-[10px] text-fieldiq-muted">Pending</p>
          </div>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <DollarSign className="w-4 h-4 text-fieldiq-accent mx-auto mb-1" />
            <p data-testid="maintenance-total-cost" className="text-lg font-bold text-fieldiq-accent">{fmt(totalCost)}</p>
            <p className="text-[10px] text-fieldiq-muted">Total Cost</p>
          </div>
        </div>

        {isEmpty ? (
          <div className="text-center py-16">
            <Wrench size={40} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-fieldiq-muted text-sm">No maintenance records for this period.</p>
          </div>
        ) : (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs">
                    <th className="text-left px-4 py-2.5 font-medium">Asset</th>
                    <th className="text-left px-4 py-2.5 font-medium">Issue</th>
                    <th className="text-left px-4 py-2.5 font-medium">Status</th>
                    <th className="text-left px-4 py-2.5 font-medium">Created</th>
                    <th className="text-left px-4 py-2.5 font-medium">Completed</th>
                    <th className="text-right px-4 py-2.5 font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrders.map((w: any, i: number) => (
                    <tr key={i} className="border-b border-fieldiq-border/50">
                      <td className="px-4 py-2.5 text-white font-medium">{w.asset_name}</td>
                      <td className="px-4 py-2.5 text-fieldiq-muted">{w.issue_description}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={w.status || 'Pending'} /></td>
                      <td className="px-4 py-2.5 text-fieldiq-muted">{w.created_at}</td>
                      <td className="px-4 py-2.5 text-fieldiq-muted">{w.completed_at || '—'}</td>
                      <td className="px-4 py-2.5 text-right text-white">{fmt(w.cost || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-fieldiq-accent border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
}
